import { Link } from "react-router-dom";
import { ArrowRight, Crown, Feather } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PublicHeader } from "@/components/PublicHeader";
import { FeatureRow } from "@/components/FeatureRow";
import { FREE_EXTRAS, FREE_FEATURES, PRO_FEATURES, WRITING_FEATURES } from "@/lib/features";

const GroupHeader = ({ title, note, first }) => (
  <div className={`${first ? "" : "mt-28 "}mb-14 flex flex-wrap items-baseline gap-x-4 gap-y-1`} data-testid={`features-group-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <h3 className="font-display text-3xl text-amber-200 italic">{title}</h3>
    {note && <p className="text-sm text-slate-500">{note}</p>}
  </div>
);

const TierHeader = ({ id, icon: Icon, label, title, text }) => (
  <header id={id} className="scroll-mt-24 border-t border-line pt-14 mb-16 grid lg:grid-cols-12 gap-6" data-testid={`features-${id}-header`}>
    <div className="lg:col-span-5">
      <p className="flex items-center gap-2 font-code text-xs uppercase tracking-[0.3em] text-amber-400"><Icon className="w-4 h-4" />{label}</p>
      <h2 className="font-display text-4xl sm:text-5xl text-amber-50 mt-3">{title}</h2>
    </div>
    <p className="lg:col-span-6 lg:col-start-7 self-end text-slate-400 leading-relaxed">{text}</p>
  </header>
);

export default function FeaturesPage() {
  const { user } = useAuth();
  return (
    <div className="relative z-10 min-h-screen" data-testid="features-page">
      <PublicHeader testId="features-brand-link" />
      <main className="px-5 md:px-14 pb-28 max-w-7xl mx-auto">
        <section className="pt-16 md:pt-24 pb-20 max-w-3xl">
          <p className="rise font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-4">Features</p>
          <h1 className="rise font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50 leading-[1.05]" style={{ animationDelay: "80ms" }}>
            Everything your world needs, <em className="text-amber-400 font-normal">from the first name to the family tree.</em>
          </h1>
          <p className="rise mt-6 text-slate-400 max-w-xl" style={{ animationDelay: "160ms" }}>Every wiki and writing tool is free. Pro adds room for more, people to build with and a look of your own.</p>
          <div className="rise mt-8 inline-flex rounded-full border border-line bg-inset p-1" style={{ animationDelay: "240ms" }}>
            <a href="#free" className="px-5 py-2 rounded-full text-sm font-semibold bg-amber-500 text-onaccent" data-testid="features-jump-free">Free</a>
            <a href="#pro" className="px-5 py-2 rounded-full text-sm text-slate-300 hover:text-amber-200 transition-colors" data-testid="features-jump-pro">Pro</a>
          </div>
        </section>

        <TierHeader id="free" icon={Feather} label="Free · $0 forever" title="The whole toolkit, free"
          text="Two worlds, three works and every feature below: pages, maps, trees, charts and timelines, plus the full writing desk. No trial, no card." />
        <GroupHeader title="For worldbuilders" first />
        <div className="space-y-28">
          {FREE_FEATURES.map((f, i) => <FeatureRow key={f.id} f={f} flip={i % 2 === 1} />)}
        </div>
        <GroupHeader title="For writers" note="The writing desk is part of Free, with three works to start." />
        <div className="space-y-28">
          {WRITING_FEATURES.map((f, i) => <FeatureRow key={f.id} f={f} flip={i % 2 === 1} />)}
        </div>
        <div className="mt-24 grid sm:grid-cols-3 gap-px overflow-hidden rounded-2xl border border-line bg-line/60" data-testid="features-free-extras">
          {FREE_EXTRAS.map(([t, d]) => (
            <div key={t} className="bg-canvas p-6"><h3 className="font-display text-xl text-amber-100 mb-1.5">{t}</h3><p className="text-sm text-slate-400">{d}</p></div>
          ))}
        </div>

        <div className="mt-32">
          <TierHeader id="pro" icon={Crown} label="Pro · from $4.99/month" title="More room, more hands, your own look"
            text="Everything in Free, plus unlimited worlds and works, co-writers, every theme and every scene break design. Pay monthly or yearly, or give it as a gift." />
          <div className="space-y-28">
            {PRO_FEATURES.map((f, i) => <FeatureRow key={f.id} f={f} flip={i % 2 === 1} accent="text-amber-300" />)}
          </div>
        </div>

        <section className="mt-32 rounded-3xl border border-amber-500/30 bg-panel px-8 py-14 md:px-14 flex flex-col md:flex-row md:items-center justify-between gap-8" data-testid="features-cta">
          <div>
            <h2 className="font-display text-4xl text-amber-50">Start your first world tonight.</h2>
            <p className="text-slate-400 mt-2">Free forever for two worlds. Upgrade only when you need more.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to={user ? "/worlds" : "/register"} className="group inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 font-semibold text-onaccent hover:bg-amber-400 transition-colors" data-testid="features-cta-start">
              {user ? "Go to my worlds" : "Start free"} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/pricing" className="inline-flex items-center rounded-full border border-line px-6 py-3 text-slate-200 hover:border-amber-500/40 transition-colors" data-testid="features-cta-pricing">See pricing</Link>
          </div>
        </section>
      </main>
    </div>
  );
}
